import { NavLink, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import $ from 'jquery';
import ReactDOM from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FilterNav from './FilterPages/FilterNav';

import TopGainers from './FilterPages/TopGainers';
import TopLosers from './FilterPages/TopLosers';
import Trending from './FilterPages/TrendingCoins';
import TopMarketCap from './FilterPages/TopMarketCap';
import DeFi from './FilterPages/DeFi';
import NFT from './FilterPages/NFT';
import AiTokens from './FilterPages/AITokens';
import Meme from './FilterPages/MemeCoins';

const apiUrl = process.env.REACT_APP_API_URL;
let debounceTimeout: NodeJS.Timeout;
const homeUrl = process.env.REACT_APP_HOME_URL;

const CACHE_KEY = 'apiData';
const CACHE_DURATION = 60 * 1000 * 4; // 4 minute
const REFRESH_THRESHOLD = 60 * 1000; // 15 seconds

// Save to cache with expiry
const setCache = (key, data, ttl) => {
  const now = Date.now();
  const expiry = now + ttl;
  const item = { data, timestamp: now, expiry };
  localStorage.setItem(key, JSON.stringify(item));
};

// Load from cache and decide if it's fresh enough
const getCache = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  const { data, timestamp, expiry } = JSON.parse(cached);
  const now = Date.now();

  if (now > expiry) {
    localStorage.removeItem(key);
    return null;
  }

  const isStale = now - timestamp > REFRESH_THRESHOLD;

  return { data, isStale };
};


// Utility: Generate QuickChart.io chart URL
const getChartUrl = (data, label, clr = 'Data') => {
  if (!data || data.length === 0) return '';

  let chart_clr = '';
  if(clr > 0) {
  	chart_clr = 'green';
  }
  else {
  	chart_clr = 'red';
  }
  const chartData = {
    type: 'line',
    data: {
      labels: data.map((_, i) => i),
      datasets: [
        {
          label: label,
          data: data,
          borderColor: chart_clr,
          fill: false,
          borderWidth: 2,
          pointRadius: 0
        },
      ],
    },
    options: {
      elements: { line: { tension: 0.4 } },
      plugins: { legend: { display: false } },
      scales: {
        x: { display: false },
        y: { display: false },
      },
    },
  };

  const encoded = encodeURIComponent(JSON.stringify(chartData));
  return `https://quickchart.io/chart?c=${encoded}`;
};

// Utility: Format numbers with commas
const formatNumber = (num) => {
  if (!num) return '-';
  return Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
};


const Home = () => {
	const [activeTab, setActiveTab] = useState('all_crypto');
	const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [skeletoncard, setSkeletonCard] = useState(true);
  const [articles, setArticles] = useState([]);
  const pagesToShow = 5;
  const startPage = Math.max(1, currentPage);
  const endPage = Math.min(totalPages, startPage + pagesToShow - 1);

  const [trendingCoins, setTrendingCoins] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [globalData, setGlobalData] = useState(null);
  
  const [marketCap, setMarketCap] = useState(null);
  const [marketCapChange, setMarketCapChange] = useState(null);
  const [volume, setVolume] = useState(null);
  const [volumeChange, setVolumeChange] = useState(null);
  const [marketCapArray, setMarketCapArray] = useState([]);
  const [volumeArray, setVolumeArray] = useState([]);

  const handlePageChange = (page: number) => {
	  if (page >= 1 && page <= totalPages) {
	    setCurrentPage(page);
	  }
	};

  const fetchResults = async () => {
    setLoading(false)
    const query = new URLSearchParams({ page_number: currentPage }).toString();
    try {
      const response = await fetch(`${apiUrl}/get-coins-data?${query}`);
      const data = await response.json();
      setCache(CACHE_KEY, data, CACHE_DURATION);
      setResults(data.resluts);
      setTotalPages(data.total_pages);
      setLoading(false);      
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  

  const fetchMarketData = async () => {
    try {
      // 1. Global Market Cap & Volume Data
      const res = await fetch(`${apiUrl}/get-global-data`);
      const { data } = await res.json();

      setMarketCap(data.total_market_cap.usd);
      setMarketCapChange(data.market_cap_change_percentage_24h_usd);
      setVolume(data.total_volume.usd);

      // 2. Market Cap & Volume History (Bitcoin as reference)
      const chartRes = await fetch(`${apiUrl}/get-chartres-data`);
      const chartData = await chartRes.json();

      // Extract values
      const marketCaps = chartData.market_caps.map((entry) => entry[1]);
      const volumes = chartData.total_volumes.map((entry) => entry[1]);

      // Downsample to stay under QuickChart 288 point limit
      const downsample = (arr, step = 6) => arr.filter((_, i) => i % step === 0);

      setMarketCapArray(downsample(marketCaps));
      setVolumeArray(downsample(volumes));

      // Calculate % change for volume
      const volStart = volumes[0];
      const volEnd = volumes[volumes.length - 1];
      const volChange = ((volEnd - volStart) / volStart) * 100;
      setVolumeChange(volChange);

      setCache('MarketCapArray', downsample(marketCaps), CACHE_DURATION);
			setCache('VolumeArray', downsample(volumes), CACHE_DURATION);
			setCache('VolumeChange', volChange, CACHE_DURATION);

    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(false);
      
      // Fetch trending coins
      const trendingResponse = await fetch(`${apiUrl}/get-trendingcoin-data`);
      const trendingData = await trendingResponse.json();
      
      // Fetch top gainers
      const gainersResponse = await fetch(`${apiUrl}/get-topgainers-data`);
      const gainersData = await gainersResponse.json();
      
      // Fetch global market data
      const globalResponse = await fetch(`${apiUrl}/get-globalcoin-data`);
      const global = await globalResponse.json();

      setTrendingCoins(trendingData.coins.slice(0, 5));
      setTopGainers(gainersData);
      setGlobalData(global.data);

      setCache('TrendingCoins', trendingData.coins.slice(0, 5), CACHE_DURATION);
      setCache('TopGainers', gainersData, CACHE_DURATION);
      setCache('GlobalData', global.data, CACHE_DURATION);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {

    const cached = getCache(CACHE_KEY);

    if (cached && !cached.isStale) {
    	console.log('===========================if')
      setResults(cached.data.resluts);
      setTotalPages(cached.data.total_pages);

      setMarketCapArray(getCache('MarketCapArray').data);
      setVolumeArray(getCache('VolumeArray').data);
      setVolumeChange(getCache('VolumeChange').data);

      setTrendingCoins(getCache('TrendingCoins').data);
      setTopGainers(getCache('TopGainers').data);
      setGlobalData(getCache('GlobalData').data);

    } else {
      fetchResults();
      fetchMarketData();
      fetchData();
      console.log('===========================else')
    }

  }, [currentPage]);


  useEffect(() => {
    const fetchRSS = async () => {
      try {
        const rssUrl = 'https://decrypt.co/feed';
        const proxyUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

        const response = await fetch(proxyUrl);
        const data = await response.json();    
        setSkeletonCard(false);
        if(data.items) {
        	setArticles(data.items);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching RSS feed:', err);
        setLoading(false);
      }
    };

    fetchRSS();
  }, []);
 
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};


return (
<div className="home_wrapper">
	{skeletoncard &&	(
		<>
			<div className="skeleton-card">
				<div className="topbanner_mainsec">
					<div className="container">
						<div className="row">
							<div className="topnews_mainsec">
								<div className="row">
									<div className="col-sm-12 col-md-8 col-lg-8 col-xl-8">
										<div className="news_mainsec">
											<div className="imgbox">
												<div className="skeleton skeleton-thumbnail"></div>
											</div>
											
											<div className="contntbox">
												<div className="skeleton skeleton-title"></div>
												<div className="skeleton skeleton-text"></div>
												<div className="skeleton skeleton-text"></div>
												<div className="skeleton skeleton-text"></div>
												
												<div className="time_box">
													<div className="skeleton skeleton-small-text"></div>
												</div>
											</div>
										</div>
									</div>
									
									<div className="col-sm-12 col-md-4 col-lg-4 col-xl-4">
										<div className="side_newsbox">
											{articles.map((item, index) => (
												(index === 1 || index === 2) && (
													<div className="small_newsbox">
														<div className="contntbox">
															<div className="skeleton skeleton-title"></div>
															
															<div className="time_box">
																<div className="skeleton skeleton-small-text"></div>
															</div>
														</div>
													</div>
												)
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				
				<div className="marketprice_mainsec">
					<div className="container">
						<div className="row">
							<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
								<div className="tranding_coins coinbox">
									<div className="titlebox">
										<div className="skeleton skeleton-title"></div>
										
										<div className="skeleton skeleton-text"></div>
									</div>
									
									<ul>
										{trendingCoins.map((coin) => {
											const change24h = coin.item.data.price_change_percentage_24h.usd;
											return (
												<li key={coin.item.id}>
													<div className="skeleton skeleton-title"></div>
												
													<div className="skeleton skeleton-text"></div>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						
							<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
								<div className="tranding_coins coinbox">
									<div className="titlebox">
										<div className="skeleton skeleton-title"></div>
										
										<div className="skeleton skeleton-text"></div>
									</div>
									
									<ul>
										{trendingCoins.map((coin) => {
											const change24h = coin.item.data.price_change_percentage_24h.usd;
											return (
												<li key={coin.item.id}>
													<div className="skeleton skeleton-title"></div>
												
													<div className="skeleton skeleton-text"></div>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						
							<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
								<div className="marketprice_volume">
									<div className="marketcap coinbox">
										<div className="titlebox">
											<div className="skeleton skeleton-title"></div>
										</div>
										
										<div className="price_graphbox">
											<div className="pricebox">
												<div className="skeleton skeleton-title"></div>
											</div>
											
											<div className="graphimg">
												<div className="skeleton skeleton-thumbnail"></div>
											</div>
										</div>
									</div>
						  
									<div className="marketcap coinbox">
										<div className="titlebox">
											<div className="skeleton skeleton-title"></div>
										</div>
										
										<div className="price_graphbox">
											<div className="pricebox">
												<div className="skeleton skeleton-title"></div>
											</div>
											
											<div className="graphimg">
												<div className="skeleton skeleton-thumbnail"></div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				
				<div className="crypto_price_table">
					<div className="container">
						<div className="table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th scope="col"></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
										<th scope="col"><div className="skeleton skeleton-title"></div></th>
									</tr>
								</thead>
								
								<tbody>
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
									
									<tr>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
										<td><div className="skeleton skeleton-small-text"></div></td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</>
	)}
	<div className="topbanner_mainsec">
		<div className="container">
			<div className="row">
				<div className="topnews_mainsec">
					<div className="row">
						<div className="col-sm-12 col-md-8 col-lg-8 col-xl-8">
							{articles.map((item, index) => (
							  index === 0 && (
								<div className="news_mainsec">
									<div className="imgbox">
										<img alt={item.title} src={item.enclosure.link} />
									</div>
									<div className="contntbox">
										<h2><a href={`${homeUrl}artical/${item.title.replace(/[^a-zA-Z0-9 ]/g, '')}?source=Decrypt`}>{item.title}</a></h2>
										<p className="article_description" dangerouslySetInnerHTML={{ __html: item.description }}></p>
										
										<div className="time_box">
											<span className="time_txt">{formatDate(item.pubDate)}</span>
										</div>
									</div>
								</div>
								)
							))}
						</div>
						
						<div className="col-sm-12 col-md-4 col-lg-4 col-xl-4">
							<div className="side_newsbox">
								{articles.map((item, index) => (
									(index === 1 || index === 2) && (
										<div className="small_newsbox">
											<div className="contntbox">
												<h2><a href={`${homeUrl}artical/${item.title.replace(/[^a-zA-Z0-9 ]/g, '')}?source=Decrypt`}>{item.title}</a></h2>
												
												<div className="time_box">
													<span className="time_txt">{formatDate(item.pubDate)}</span>
												</div>
											</div>
										</div>
									)
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	
	<div className="marketprice_mainsec">
        <div className="container">
			<div className="row">
				<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
					<div className="tranding_coins coinbox">
						<div className="titlebox">
							<h3>Trending Coins</h3>
							
							<a href="/trending-coins" onClick={(e) => {
								e.preventDefault();
								navigate('/trending-coins');
							}}>View More</a>
						</div>
                
						<ul>
							{trendingCoins.map((coin) => {
								const change24h = coin.item.data.price_change_percentage_24h.usd;
								return (
								  <li key={coin.item.id}>
									<div className="coinname">
									  <img 
										alt={coin.item.name} 
										src={coin.item.small} 
										onError={(e) => {
										  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
										}}
									  />
									  <a href={`/coin/${coin.item.id}`} onClick={() => navigate(`/coin/${coin.item.id}`)}><span >{coin.item.name}</span></a>
									</div>
									
									<div className="pricebox">
									  <h2>
										${coin.item.data.price.toLocaleString('en-US', {
										  minimumFractionDigits: 2,
										  maximumFractionDigits: 4
										})}
										<span className={`updown_txt ${
										  change24h >= 0 ? 'upcoin' : 'downcoin'
										}`}>
										  <i className={`fas fa-caret-${
											change24h >= 0 ? 'up' : 'down'
										  }`}></i>
										  <span>{Math.abs(change24h).toFixed(2)}%</span>
										</span>
									  </h2>
									</div>
								  </li>
								);
							})}
						</ul>
					</div>
				</div>
            
				<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
					<div className="tranding_coins coinbox">
						<div className="titlebox">
							<h3>Top Gainers</h3>
							
							<a href="/top-gainers" onClick={(e) => {
								e.preventDefault();
								navigate('/top-gainers');
							}}>View More</a>
						</div>
                
						<ul>
							{topGainers.map((coin) => {
								const change24h = coin.price_change_percentage_24h;
								return (
								  <li key={coin.id}>
									<div className="coinname">
									  <img 
										alt={coin.name} 
										src={coin.image} 
										onError={(e) => {
										  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
										}}
									  />
									  
										<a href={`/coin/${coin.id}`} onClick={() => navigate(`/coin/${coin.id}`)}><span >{coin.name}</span></a>
									</div>
									
									<div className="pricebox">
									  <h2>
										${coin.current_price.toLocaleString('en-US', {
										  minimumFractionDigits: 2,
										  maximumFractionDigits: 6
										})}
										<span className={`updown_txt ${
										  change24h >= 0 ? 'upcoin' : 'downcoin'
										}`}>
										  <i className={`fas fa-caret-${
											change24h >= 0 ? 'up' : 'down'
										  }`}></i>
										  <span>{Math.abs(change24h).toFixed(2)}%</span>
										</span>
									  </h2>
									</div>
								  </li>
								);
							})}
						</ul>
					</div>
				</div>
            
				<div className="col-sm-12 col-md-12 col-lg-4 col-xl-4">
					{globalData && (
						<>
							<div className="marketprice_volume">
								<div className="row">
									<div className="col-sm-12 col-md-6 col-lg-12 col-xl-12">
										<div className="marketcap coinbox">
											<div className="titlebox">
												<h3>Market Cap</h3>
											</div>
								
											<div className="price_graphbox">
												<div className="pricebox">
													<h2>
														{formatNumber(marketCap)}
														<span className={`updown_txt ${
															marketCapChange >= 0 
																? 'upcoin' 
																: 'downcoin'
															}`}>
															
															<i className={`fas fa-caret-${
															  marketCapChange >= 0 
																? 'up' 
																: 'down'
															}`}></i>
															
															<span>
																{Math.abs(marketCapChange).toFixed(2)}%
															</span>
														</span>
													</h2>
												</div>
												
												<div className="graphimg">
													<img
													  src={getChartUrl(marketCapArray, 'Market Cap', marketCapChange)}
													  alt="Market Cap Chart"
													  width="300"
													  height="150"
													/>
												</div>
											</div>
										</div>
									</div>
								
									<div className="col-sm-12 col-md-6 col-lg-12 col-xl-12">
										<div className="marketcap coinbox">
											<div className="titlebox">
											  <h3>24h Trading Volume</h3>
											</div>
								
											<div className="price_graphbox">
											  <div className="pricebox">
												<h2>
												  {formatNumber(volume)}
												  <span className={`updown_txt ${volumeChange >= 0 ? 'upcoin' : 'downcoin'}`}>
													<i className={`fas fa-caret-${volumeChange >= 0 ? 'up' : 'down'}`}></i>
													<span>
													  {Math.abs(volumeChange).toFixed(2)}%
													</span>
												  </span>
												</h2>
											  </div>
											  
											  <div className="graphimg">
												<img
												  src={getChartUrl(volumeArray, 'Volume', volumeChange)}
												  alt="Volume Chart"
												  width="300"
												  height="150"
												/>
											  </div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
        </div>
    </div>
	
	
	<FilterNav activeTab={activeTab} setActiveTab={setActiveTab} />
	
	{activeTab === 'all_crypto' &&
	<div className="crypto_price_table">
		<div className="container">
			<div className="table-responsive">
				<table className="table">
					<thead>
						<tr>
							<th scope="col"></th>
							<th scope="col">#</th>
							<th scope="col">Coin</th>
							<th scope="col">Price</th>
							<th scope="col">1h</th>
							<th scope="col">24h</th>
							<th scope="col">7d</th>
							<th scope="col">24h Volume</th>
							<th scope="col">Market Cap</th>
							<th scope="col">Last 7 Days</th>
						</tr>
					</thead>
					
					<tbody>
						{results.length ? (
                        results.map((result, index) => (
                          <tr key={result.id}>
							<td><i className="far fa-star"></i></td>
							<td>{((currentPage-1)*100)+index+1}</td>
							<td>
								<div className="cryptonamebtn">
									<div className="cryptoname">
										<img alt="Bitcoin Logo" src={result.image} />
										<span className="coinname" style={{ cursor: "pointer" }} onClick={() => navigate(`/coin/${result.id}`)}>{result.name} <span className="cointxt">{result.symbol}</span></span>
									</div>
								</div>
							</td>
							<td>${result.current_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td>
							  {result.price_change_percentage_1h_in_currency != null
							    ? result.price_change_percentage_1h_in_currency.toLocaleString('en-US', {
							        minimumFractionDigits: 2,
							        maximumFractionDigits: 2
							      }) + '%'
							    : 'N/A'}
							</td>
							<td>
							  {result.price_change_percentage_24h != null
							    ? result.price_change_percentage_24h.toLocaleString('en-US', {
							        minimumFractionDigits: 2,
							        maximumFractionDigits: 2
							      }) + '%'
							    : 'N/A'}
							</td>
							<td>
							  {result.price_change_percentage_7d_in_currency != null
							    ? result.price_change_percentage_7d_in_currency.toLocaleString('en-US', {
							        minimumFractionDigits: 2,
							        maximumFractionDigits: 2
							      }) + '%'
							    : 'N/A'}
							</td>
							<td>${result.total_volume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td>${result.market_cap.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
							<td><img alt={result.name} src={`https://www.coingecko.com/coins/${result.coin_id}/sparkline.svg`} /></td>
						</tr>
						))
                      ) : (
                        <tr>
                          <td colSpan="10">No campaigns found</td>
                        </tr>
                      )}
					</tbody>

					<tfoot>
						<tr>
							<td colSpan="10">
								<div className="flex items-center space-x-1 pagination_box">
				                    <button
				                      onClick={() => handlePageChange(currentPage - 1)}
				                      disabled={currentPage === 1}
				                      className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 disabled:opacity-50 previous_btn"
				                    >
				                      <i className="fas fa-arrow-left"></i>
				                    </button>

				                    {/* Dynamic Page Numbers */}
				                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
				                      <button
				                        key={page}
				                        onClick={() => handlePageChange(page)}
				                        className={`w-8 h-8 flex items-center justify-center rounded-md ${
				                          currentPage === page ? "bg-blue-600 text-white" : "border border-gray-300 text-gray-700"
				                        }`}
				                      >
				                        {page}
				                      </button>
				                    ))}

				                    {/* Next Button */}
				                    <button
				                      onClick={() => handlePageChange(currentPage + 1)}
				                      disabled={currentPage === totalPages}
				                      className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 disabled:opacity-50 next_btn"
				                    >
				                      <i className="fas fa-arrow-right"></i>
				                    </button>
				                  </div>
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	</div>
	}
	{activeTab === 'top_gainers' && <TopGainers />}
  {activeTab === 'top_losers' && <TopLosers />}
  {activeTab === 'trending' && <Trending activeTab={activeTab}/>}
  {activeTab === 'top_marketcap' && <TopMarketCap />}
  {activeTab === 'defi' && <DeFi />}
  {activeTab === 'nft' && <NFT />}
  {activeTab === 'ai' && <AiTokens />}
  {activeTab === 'meme' && <Meme />}
</div>

);
};

export default Home;